import React from 'react';
import { BELT_COLORS } from '../../utils/belt-colors';
import { BeltConfig } from '../../types/belt';

/**
 * Props for the Belt component
 * Supports both simple color/stripes format and full BeltConfig objects
 */
export interface BeltProps {
  /**
   * Belt color (white, yellow, orange, green, blue, red, purple, brown, black)
   * Used when beltConfig is not provided
   */
  color?: string;

  /**
   * Number of stripes on the belt (0-10)
   * Used when beltConfig is not provided
   */
  stripes?: number;

  /**
   * Full belt configuration object (takes precedence over color/stripes)
   * Includes pattern/contrast support matching original Marune app
   */
  beltConfig?: BeltConfig;

  /**
   * Optional custom classNames for styling
   */
  className?: string;

  /**
   * Whether to show the belt component
   */
  visible?: boolean;

  /**
   * Optional size variant
   */
  size?: 'small' | 'medium' | 'large';
}

/**
 * Get hex color value from color name
 */
const getColorHex = (colorName?: string): string => {
  if (!colorName) return BELT_COLORS.white.backgroundColorCode;

  const lowerName = colorName.toLowerCase();

  // Check in BELT_COLORS
  if (BELT_COLORS[lowerName]) {
    return BELT_COLORS[lowerName].backgroundColorCode;
  }

  // If it's already a hex color, return as-is
  if (lowerName.startsWith('#')) {
    return lowerName;
  }

  // Default to white
  return BELT_COLORS.white.backgroundColorCode;
};

/**
 * Belt component that displays a martial arts belt with stripes and patterns
 *
 * Supports:
 * - All 9 belt colors from original app (white, yellow, orange, green, blue, red, purple, brown, black)
 * - Stripes (0-10) with configurable colors
 * - Contrast patterns (alternate, horizontal stripes, etc.) for special belts like coral
 * - Adult/Junior level belts
 */
const Belt: React.FC<BeltProps> = ({
  color = 'white',
  stripes = 0,
  beltConfig,
  className = '',
  visible = true,
  size = 'medium',
}) => {
  // Don't render if not visible
  if (!visible) return null;

  // Extract values from beltConfig if provided, otherwise use props
  const baseColor = beltConfig?.baseColor || color;
  // Ensure stripeCount is a valid number (never NaN).
  // stipeCount fallback: raw Firebase configs store the rules-mandated misspelling.
  const rawStripeCount = beltConfig?.stripeCount ?? beltConfig?.stipeCount ?? stripes;
  const stripeCount = typeof rawStripeCount === 'number' && !isNaN(rawStripeCount) ? rawStripeCount : 0;
  const contrastColor = beltConfig?.contrastColor;
  const contrastType = beltConfig?.contrastType;
  const stripeColor = beltConfig?.stripeColor || 'white';
  const stripeBgColor = beltConfig?.stripeBgColor || (baseColor.toLowerCase() === 'black' ? 'red' : 'black');

  // Normalize color to lowercase for lookups
  const normalizedColor = baseColor.toLowerCase();

  // Get base color hex
  const baseColorHex = getColorHex(normalizedColor);
  const contrastColorHex = contrastColor ? getColorHex(contrastColor) : 'transparent';
  const stripeColorHex = getColorHex(stripeColor);
  const stripeBgColorHex = getColorHex(stripeBgColor);

  // Sizes for different variants
  const sizeConfig = {
    small: { height: 12, minWidth: 128 },
    medium: { height: 16, minWidth: 160 },
    large: { height: 20, minWidth: 208 },
  };

  const { height, minWidth } = sizeConfig[size];

  // Limit stripes to a maximum of 10
  const validStripes = Math.min(Math.max(0, stripeCount), 10);

  // Determine if white belt (needs border)
  const isWhiteBelt = normalizedColor === 'white';

  /**
   * Render contrast/pattern overlay based on contrastType
   */
  const renderContrast = () => {
    if (!contrastType || !contrastColor) return null;

    switch (contrastType) {
      case 'alternate':
        // Alternating blocks pattern (e.g., coral belt black/red)
        return (
          <div
            className="absolute inset-0 flex pointer-events-none"
            style={{ zIndex: 1 }}
          >
            {[0, 1, 2, 3].map(i => (
              <div
                key={`contrast-${i}`}
                className="flex-1"
                style={{
                  backgroundColor: i % 2 === 1 ? contrastColorHex : 'transparent',
                }}
              />
            ))}
          </div>
        );

      case 'centered horizontal stripe':
        // Single horizontal stripe through center
        return (
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              top: '35%',
              height: '30%',
              backgroundColor: contrastColorHex,
              zIndex: 1,
            }}
          />
        );

      case 'top horizontal stripe':
        // Stripe at top of belt
        return (
          <div
            className="absolute left-0 right-0 top-0 pointer-events-none"
            style={{
              height: '25%',
              backgroundColor: contrastColorHex,
              zIndex: 1,
            }}
          />
        );

      case '2x horizontal stripes':
        // Double horizontal stripes
        return (
          <>
            <div
              className="absolute left-0 right-0 pointer-events-none"
              style={{
                top: '20%',
                height: '15%',
                backgroundColor: contrastColorHex,
                zIndex: 1,
              }}
            />
            <div
              className="absolute left-0 right-0 pointer-events-none"
              style={{
                bottom: '20%',
                height: '15%',
                backgroundColor: contrastColorHex,
                zIndex: 1,
              }}
            />
          </>
        );

      case 'character':
        // Special character belt (kids/novelty)
        // Simple implementation - can be enhanced later
        return null;

      default:
        return null;
    }
  };

  /**
   * Render stripes section
   */
  const renderStripes = () => {
    if (validStripes === 0) return null;

    // Calculate width needed for stripes
    const stripeWidth = 6;
    const stripeSpacing = 2;
    const stripePadding = 8;
    const stripeSectionWidth = (validStripes * stripeWidth) + ((validStripes - 1) * stripeSpacing) + (stripePadding * 2);

    return (
      <div
        className="absolute right-1 top-0 bottom-0 flex items-center justify-end px-2"
        style={{
          backgroundColor: stripeBgColorHex,
          minWidth: Math.max(stripeSectionWidth, 40),
          zIndex: 2,
        }}
      >
        {Array.from({ length: validStripes }).map((_, index) => (
          <div
            key={`stripe-${index}`}
            style={{
              height: '60%',
              width: stripeWidth,
              backgroundColor: stripeColorHex,
              marginLeft: index > 0 ? stripeSpacing : 0,
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={`relative flex overflow-hidden rounded-sm ${className}`}
      style={{
        height,
        minWidth,
        backgroundColor: baseColorHex,
        border: isWhiteBelt ? '1px solid #d1d5db' : 'none',
      }}
      aria-label={`${beltConfig?.name || baseColor} belt${beltConfig?.rank ? ` - ${beltConfig.rank}` : ''}${validStripes > 0 ? ` with ${validStripes} stripes` : ''}`}
    >
      {/* Contrast pattern overlay */}
      {renderContrast()}

      {/* Stripes */}
      {renderStripes()}
    </div>
  );
};

export default Belt;
